import { useState } from 'react'
import { useParams } from 'react-router'
import { MonitorSmartphone } from 'lucide-react'
import { leases as leaseApi } from '@/api/endpoints'
import type { Lease } from '@/api/types'
import { Err } from '@/components/Err'
import { ToneBadge } from '@/components/ToneBadge'
import { Crumb, Empty, PageHead, Section } from '@/components/layout/Page'
import { UNREACHABLE_WORD, isClosed, isUnreachable, leaseWord, leaseToneOf, mayTag, vmToneOf, vmWord } from '@/domain/lease'
import { usePoll } from '@/hooks/usePoll'
import { useSession } from '@/hooks/useSession'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { AppPanel } from '../AppPanel'
import { GuestKeysLine } from '../GuestKeysLine'
import { KeyRotateChip } from '../KeyRotateChip'
import { ConsoleChip } from '../ConsoleDialog'
import { AccessPanel, KeyDownload } from '../vm/AccessPanel'
import { ACTION, ACTION_PLAIN } from '../vm/chips'
import { PowerActions } from '../vm/PowerActions'
import { VmStatusPanel } from '../vm/VmStatusPanel'
import { TagEditChip, VmTags } from '../vm/VmTags'
import { ChangeDialog } from './ChangeDialog'
import { ChangeLine } from './ChangeLine'

const LEASE_POLL_MS = 5000

// 전원 조작의 낱말과 조건은 `domain/lease.ts` 의 `POWER` 다 — 관리자 VM 목록의 일괄 조작도 그 표를 읽는다.

// 내 VM 하나 (B-9). 제목 줄(태그, 상태 배지와 그 아래의 조작 칩) · 상태(사용량 게이지·입출력) · 접속 · 앱. 임대 id 가 키다.
// 여기 없는 것: VMID, 노드·클러스터·풀, PVE 관측값, PCI 주소, 이벤트, 내부 id. 사설 주소는 접속 칸에 백엔드가 보낼 때만 선다.
// 접속은 게이트웨이를 지나는 두 가지 형태 — 접속 명령어 한 줄과 ~/.ssh/config 조각 — 뿐이다.
// 기계에 하는 조작은 전원(실행·종료·정지·재부팅·리셋)과 반납이다. 전원 요청은 PVE 의 작업이 끝날 때까지 기다린다.

export default function MyVm() {
  const { id } = useParams()
  // 경로의 임대가 바뀌면 처음부터 다시 읽는다 — 앞 VM 의 읽기·열린 창이 다음 VM 에 남지 않는다.
  return <Detail key={id} id={id} />
}

function Detail({ id }: { id: string | undefined }) {
  const me = useSession()
  const lease = usePoll(() => (id ? leaseApi.get(id) : Promise.resolve(null)), useFallback(LEASE_POLL_MS), [id])
  const app = usePoll(() => (id ? leaseApi.app(id) : Promise.resolve(null)), useFallback(LEASE_POLL_MS), [id])
  useRefetchOn(
    (c) => c.kind === 'lease' && c.id === id,
    () => {
      lease.refresh()
      app.refresh()
    },
  )
  const l = lease.data

  if (lease.error) {
    return (
      <>
        <Crumb steps={[{ label: '내 VM', to: '/' }, { label: '' }]} />
        <Err error={lease.error} />
      </>
    )
  }
  if (!l) return <Empty>{lease.loading ? '불러오는 중' : '없는 VM'}</Empty>

  return (
    <>
      <Crumb steps={[{ label: '내 VM', to: '/' }, { label: l.name }]} />
      <PageHead
        icon={<MonitorSmartphone className="size-5" />}
        kind="VM"
        title={l.name}
        right={
          <div className="mt-1 flex flex-col items-end gap-2">
            <div className="flex items-center gap-1.5">
              <ToneBadge tone={leaseToneOf(l.state)}>{leaseWord(l.state)}</ToneBadge>
              {l.state === 'active' && l.vm?.state === 'stopped' ? (
                <ToneBadge tone={vmToneOf('stopped')}>{vmWord('stopped')}</ToneBadge>
              ) : null}
              {l.state === 'active' && isUnreachable(l.vm) ? <ToneBadge tone="warn">{UNREACHABLE_WORD}</ToneBadge> : null}
            </div>
            {!isClosed(l) ? <Actions lease={l} onChanged={lease.refresh} /> : null}
            {/* 태그는 소유자의 낱말이라 반납된 VM 에도 남는다 — 다른 조작이 없는 끝난 VM 에서는 이 칩 하나가 선다. */}
            {isClosed(l) && mayTag(l, me) ? <TagEditChip lease={l} onSaved={lease.refresh} className={`${ACTION} ${ACTION_PLAIN}`} /> : null}
          </div>
        }
        facts={
          l.tags.length > 0 || l.team ? (
            <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
              <VmTags tags={l.tags} />
              {l.team ? <span>팀 {l.team.name}</span> : null}
            </span>
          ) : null
        }
      />

      <Section title="상태">
        {!isClosed(l) ? <ChangeLine lease={l} onChanged={lease.refresh} /> : null}
        <VmStatusPanel lease={l} />
      </Section>

      <Section
        title="접속"
        right={
          <div className="flex items-center gap-1.5">
            <KeyRotateChip lease={l} onChanged={lease.refresh} />
            <KeyDownload lease={l} />
          </div>
        }
      >
        <AccessPanel lease={l} />
        <GuestKeysLine lease={l} rotate />
      </Section>

      <Section title="앱">
        <Err error={app.error} />
        {app.data ? (
          <AppPanel key={app.data.port ?? ''} lease={l} app={app.data} onSaved={app.refresh} />
        ) : app.loading ? (
          <Empty>불러오는 중</Empty>
        ) : null}
      </Section>
    </>
  )
}

// 제목 줄 오른쪽, 배지 아래의 조작 칩 한 줄: 콘솔, 지금 상태에 맞는 전원 칩들, 태그 수정·변경 신청과 반납.
// 전원과 반납은 관리자 VM 상세와 같은 `PowerActions` 다.
function Actions({ lease: l, onChanged }: { lease: Lease; onChanged: () => void }) {
  const me = useSession()
  return (
    <PowerActions
      lease={l}
      onChanged={onChanged}
      endTo="/"
      lead={(busy) => <ConsoleChip lease={l} className={`${ACTION} ${ACTION_PLAIN}`} disabled={busy} />}
      extra={(busy) => (
        <>
          {mayTag(l, me) ? <TagEditChip lease={l} onSaved={onChanged} className={`${ACTION} ${ACTION_PLAIN}`} /> : null}
          <ChangeButton lease={l} onRequested={onChanged} disabled={busy} />
        </>
      )}
    />
  )
}

// 변경 신청(B-27). 기다리는 변경이 있으면 칩이 잠긴다 — 임대당 하나만 연다.
function ChangeButton({ lease: l, onRequested, disabled = false }: { lease: Lease; onRequested: () => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        className={`${ACTION} ${ACTION_PLAIN}`}
        onClick={() => setOpen(true)}
        disabled={disabled || l.state !== 'active' || !!l.pending_change}
      >
        변경 신청
      </button>
      <ChangeDialog lease={l} open={open} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onRequested() }} />
    </>
  )
}
